import { useState, useEffect, useCallback } from 'react';

const useLeadLabels = ({ clientId, authenticatedAxios, conversations, incompleteConversations, showToast }) => {
  // Lead label state
  const [leadLabels, setLeadLabels] = useState([]);
  const [threadLabelMap, setThreadLabelMap] = useState({});
  const [labelPickerThreadId, setLabelPickerThreadId] = useState(null);
  const [labelPickerPosition, setLabelPickerPosition] = useState({ top: 0, left: 0 });
  const [isEditLabelsVisible, setIsEditLabelsVisible] = useState(false);
  const [editLabelsData, setEditLabelsData] = useState([]);
  // Multi-select label filter. Empty array = "all labels" (no label filtering).
  // Entries are label names, plus the sentinel '__none__' for "No label".
  // Semantics inside this group are OR (a lead carries exactly one label, so
  // picking several means "show me any of these"); it still ANDs with the
  // search text / online / activity-period filters.
  const [labelFilterValues, setLabelFilterValues] = useState([]);

  const toggleLabelFilter = useCallback((name) => {
    setLabelFilterValues(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  }, []);

  const clearLabelFilter = useCallback(() => setLabelFilterValues([]), []);

  // Fetch lead labels configuration
  useEffect(() => {
    let cancelled = false;
    const fetchLeadLabels = async () => {
      if (!clientId) return;
      try {
        const response = await authenticatedAxios.get(`/funnel/lead-labels/${encodeURIComponent(clientId)}`);
        if (!cancelled && response.data?.labels) {
          setLeadLabels(response.data.labels);
        }
      } catch (error) {
        console.error('Failed to fetch lead labels:', error);
      }
    };
    fetchLeadLabels();
    return () => { cancelled = true; };
  }, [clientId, authenticatedAxios]);

  // Build threadId -> leadLabel mapping from conversations that have funnel data
  useEffect(() => {
    const newMap = {};
    if (incompleteConversations?.length) {
      incompleteConversations.forEach(s => {
        if (s.conversationId && s.leadLabel) {
          newMap[s.conversationId] = s.leadLabel;
        }
      });
    }
    if (Object.keys(newMap).length > 0) {
      setThreadLabelMap(prev => ({ ...prev, ...newMap }));
    }
  }, [incompleteConversations]);

  // Bulk-fetch lead labels for conversations in the Leads tab
  // Re-triggers when conversations change OR when clientId becomes available (after auth)
  const fetchLabelsForLeads = useCallback(async () => {
    if (!conversations?.length || !clientId) return;
    const threadIds = conversations.map(c => c.threadId).filter(Boolean);
    if (threadIds.length === 0) return;
    try {
      console.log('[LeadLabels] Fetching labels for', threadIds.length, 'threads');
      const response = await authenticatedAxios.post('/funnel/lead-labels/by-threads', { threadIds });
      if (response.data?.labelMap) {
        console.log('[LeadLabels] Got labelMap with', Object.keys(response.data.labelMap).length, 'entries:', response.data.labelMap);
        setThreadLabelMap(prev => ({ ...prev, ...response.data.labelMap }));
      }
    } catch (error) {
      console.error('Failed to fetch lead labels for threads:', error);
    }
  }, [conversations, clientId, authenticatedAxios]);

  useEffect(() => {
    fetchLabelsForLeads();
  }, [fetchLabelsForLeads]);

  const getLeadLabelForThread = (tid) => threadLabelMap[tid] || null;
  const getLabelConfig = (labelName) => leadLabels.find(l => l.name === labelName);

  const handleSetLeadLabel = async (tid, labelName) => {
    try {
      await authenticatedAxios.put(`/funnel/session/by-thread/${tid}/label`, { leadLabel: labelName, expertId: clientId });
      setThreadLabelMap(prev => ({ ...prev, [tid]: labelName }));
      setLabelPickerThreadId(null);
      showToast(`Label set to "${labelName || 'None'}"`, 'success');
    } catch (error) {
      console.error('Failed to set lead label:', error);
      showToast('Failed to set label', 'error');
    }
  };

  const openLabelPicker = (e, tid) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setLabelPickerPosition({ top: rect.bottom + 4, left: rect.left });
    setLabelPickerThreadId(tid);
  };

  const handleSaveLabels = async () => {
    if (!clientId) return;
    try {
      const response = await authenticatedAxios.put(`/funnel/lead-labels/${encodeURIComponent(clientId)}`, { labels: editLabelsData });
      if (response.data?.labels) {
        setLeadLabels(response.data.labels);
      }
      setIsEditLabelsVisible(false);
      showToast('Labels updated', 'success');
    } catch (error) {
      console.error('Failed to save labels:', error);
      showToast('Failed to save labels', 'error');
    }
  };

  return {
    leadLabels,
    setLeadLabels,
    threadLabelMap,
    setThreadLabelMap,
    labelPickerThreadId,
    setLabelPickerThreadId,
    labelPickerPosition,
    setLabelPickerPosition,
    isEditLabelsVisible,
    setIsEditLabelsVisible,
    editLabelsData,
    setEditLabelsData,
    labelFilterValues,
    setLabelFilterValues,
    toggleLabelFilter,
    clearLabelFilter,
    getLeadLabelForThread,
    getLabelConfig,
    handleSetLeadLabel,
    openLabelPicker,
    handleSaveLabels,
    fetchLabelsForLeads,
  };
};

export default useLeadLabels;
